'use client';
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, History, MapPin, CalendarDays, Sparkles } from "lucide-react";
import { useT } from "../../i18n";
import { SectionKicker } from "./shared";

type DemoState = "start" | "classic" | "ai";

export function ActTwo({ onComplete }: { onComplete: () => void }) {
  const t = useT();
  const [state, setState] = useState<DemoState>("start");
  const [activeStep, setActiveStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  const classicSteps = useMemo(() => [
    t('landlink.two.step1'),
    t('landlink.two.step2'),
    t('landlink.two.step3'),
    t('landlink.two.step4'),
    t('landlink.two.step5'),
  ], [t]);

  const evidence = useMemo(() => [
    { icon: History, label: t('landlink.two.ev1.label'), value: t('landlink.two.ev1.value') },
    { icon: MapPin, label: t('landlink.two.ev2.label'), value: t('landlink.two.ev2.value') },
    { icon: CalendarDays, label: t('landlink.two.ev3.label'), value: t('landlink.two.ev3.value') },
  ], [t]);

  // Auto-illuminate classic steps
  useEffect(() => {
    if (state !== "classic") return;
    if (activeStep < classicSteps.length) {
      const timer = window.setTimeout(() => {
        setActiveStep((s) => s + 1);
      }, 520);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => setState("ai"), 800);
    return () => window.clearTimeout(timer);
  }, [state, activeStep, classicSteps.length]);

  const handleConfirm = () => {
    if (confirmed) return;
    setConfirmed(true);
    window.setTimeout(onComplete, 900);
  };

  return (
    <motion.section
      className="act act-two"
      initial={{ opacity: 0, x: "12%" }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: "-10%", filter: "blur(8px)" }}
      transition={{ duration: 0.6 }}
    >
      <div className="act-bg" />
      <div className="shade" />
      <div className="grain" />

      <div className="act-content">
        <div className="demo-stage">
          <SectionKicker>{t('landlink.two.kicker')}</SectionKicker>
          <h2>
            {t('landlink.two.titlePre')}<span>{t('landlink.two.titleHighlight')}</span>{t('landlink.two.titlePost')}
          </h2>
          <p className="body-text">{t('landlink.two.body')}</p>

          <div className="demo-card">
            <AnimatePresence mode="wait">
              {state === "start" && (
                <motion.div
                  key="start"
                  className="demo-state demo-state-start"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35 }}
                >
                  <span>{t('landlink.two.startHint')}</span>
                  <button className="demo-start-button" onClick={() => setState("classic")}>
                    {t('landlink.two.startCta')} <ArrowRight size={22} />
                  </button>
                </motion.div>
              )}

              {state === "classic" && (
                <motion.div
                  key="classic"
                  className="demo-state demo-state-classic"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="demo-state-header">
                    <small>{t('landlink.two.classicLabel')}</small>
                    <strong>{t('landlink.two.classicStepsLabel')}</strong>
                  </div>
                  <div className="demo-steps">
                    {classicSteps.map((label, index) => (
                      <motion.div
                        key={index}
                        className={`demo-step-row ${activeStep > index ? "active" : ""}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.08 }}
                      >
                        <span>0{index + 1}</span>
                        <p>{label}</p>
                      </motion.div>
                    ))}
                  </div>
                  <div className="demo-order-preview">
                    <span>{t('landlink.two.orderLabel')}</span>
                    <strong>{t('landlink.two.orderItem')}</strong>
                  </div>
                </motion.div>
              )}

              {state === "ai" && (
                <motion.div
                  key="ai"
                  className="demo-state demo-state-ai"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="demo-state-header">
                    <small>{t('landlink.two.aiLabel')}</small>
                    <strong>{t('landlink.two.aiStepsLabel')}</strong>
                  </div>

                  <div className="ai-ready">
                    <Sparkles size={28} />
                    <div className="ai-ready-order">
                      <small>{t('landlink.two.aiReadyLabel')}</small>
                      <strong>{t('landlink.two.aiOrder')}</strong>
                    </div>
                  </div>

                  <div className="ai-evidence">
                    <span>{t('landlink.two.evidenceIntro')}</span>
                    {evidence.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <motion.div
                          key={item.label}
                          className="ai-evidence-row"
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + index * 0.18 }}
                        >
                          <Icon size={18} />
                          <div>
                            <small>{item.label}</small>
                            <strong>{item.value}</strong>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <motion.button
                    className={`demo-confirm-button ${confirmed ? "confirmed" : ""}`}
                    onClick={handleConfirm}
                    disabled={confirmed}
                    animate={!confirmed ? { boxShadow: ["0 0 20px rgba(200,255,24,0.2)", "0 0 50px rgba(200,255,24,0.55)", "0 0 20px rgba(200,255,24,0.2)"] } : {}}
                    transition={{ duration: 1.2, repeat: Infinity }}
                  >
                    {confirmed ? <Check size={22} /> : <ArrowRight size={22} />}
                    {confirmed ? t('landlink.two.confirmed') : t('landlink.two.confirm')}
                  </motion.button>

                  <p className="ai-note">{t('landlink.two.aiNote')}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
